/**
 * PDF to Images conversion utility
 * Converts PDF files to compressed JPEG images (one per page) client-side
 * Uses react-pdf (which wraps PDF.js) for rendering and canvas for compression
 */

import { pdfjs } from 'react-pdf';

// react-pdf handles worker setup automatically via this import
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up the worker - react-pdf provides a pre-configured worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Compression settings for plan PDFs. Text-heavy plans need a much higher pixel
// budget than photos so users can zoom in and still read labels and notes.
const PLAN_PDF_COMPRESSION_OPTIONS = {
  renderScale: 5,
  maxWidth: 4800,
  maxHeight: 4800,
  quality: 0.95,
  minQuality: 0.85,
  targetSizeKB: 2500,
};

/**
 * Render a PDF page to a canvas and compress it
 * @param {PDFPageProxy} page - PDF.js page object
 * @param {number} pageNumber - Page number for naming
 * @param {string} baseName - Base name for the file (from PDF filename)
 * @returns {Promise<File>} - Compressed image file
 */
const renderPageToImage = async (page, pageNumber, baseName, options) => {
  // Get page dimensions
  const baseViewport = page.getViewport({ scale: 1 });
  const effectiveScale = Math.min(
    options.renderScale,
    options.maxWidth / baseViewport.width,
    options.maxHeight / baseViewport.height
  );
  const viewport = page.getViewport({ scale: effectiveScale });
  
  // Create canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Render the page
  await page.render({
    canvasContext: ctx,
    viewport: viewport,
  }).promise;
  
  // Now resize and compress like we do for regular images
  return compressCanvas(canvas, pageNumber, baseName, options);
};

/**
 * Compress a canvas to a JPEG file
 * @param {HTMLCanvasElement} sourceCanvas - The source canvas with rendered PDF page
 * @param {number} pageNumber - Page number for naming
 * @param {string} baseName - Base name for the file
 * @returns {Promise<File>} - Compressed image file
 */
const compressCanvas = (sourceCanvas, pageNumber, baseName, options) => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth,
      maxHeight,
      quality: initialQuality,
      minQuality,
      targetSizeKB,
    } = options;

    // Calculate new dimensions maintaining aspect ratio
    let width = sourceCanvas.width;
    let height = sourceCanvas.height;
    
    // Scale down if either dimension exceeds maximum
    if (width > maxWidth || height > maxHeight) {
      const widthRatio = maxWidth / width;
      const heightRatio = maxHeight / height;
      const ratio = Math.min(widthRatio, heightRatio);
      
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    
    // Create a new canvas for the resized image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw image with high quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(sourceCanvas, 0, 0, width, height);
    
    // Try progressively lower quality until we hit target size
    const attemptCompression = (quality) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress PDF page'));
            return;
          }
          
          const sizeKB = blob.size / 1024;
          
          // If still too large and quality can go lower, try again
          if (sizeKB > targetSizeKB && quality > minQuality) {
            attemptCompression(Math.max(minQuality, quality - 0.05));
            return;
          }
          
          // Create file with page number in name
          const fileName = `${baseName}_page_${pageNumber.toString().padStart(3, '0')}.jpg`;
          
          const compressedFile = new File(
            [blob],
            fileName,
            {
              type: 'image/jpeg',
              lastModified: Date.now()
            }
          );
          
          console.log(`PDF page ${pageNumber} compressed: ${(blob.size / 1024).toFixed(2)}KB (quality: ${quality.toFixed(1)})`);
          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    };
    
    // Start with initial compression quality
    attemptCompression(initialQuality);
  });
};

/**
 * Convert a PDF file to compressed JPEG images (one per page)
 * @param {File} pdfFile - The PDF file to convert
 * @param {function} onProgress - Optional callback for progress updates (page, totalPages)
 * @returns {Promise<File[]>} - Array of compressed image files
 */
export const convertPdfToImages = async (pdfFile, onProgress = null) => {
  try {
    const options = PLAN_PDF_COMPRESSION_OPTIONS;

    // Read the PDF file as ArrayBuffer
    const arrayBuffer = await pdfFile.arrayBuffer();
    
    // Load the PDF document using pdfjs from react-pdf
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    
    const numPages = pdf.numPages;
    const images = [];
    
    // Get base name from PDF filename (remove .pdf extension)
    const baseName = pdfFile.name.replace(/\.pdf$/i, '');
    
    console.log(`Converting PDF "${pdfFile.name}" with ${numPages} pages...`);
    
    // Process each page
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const imageFile = await renderPageToImage(page, pageNum, baseName, options);
      images.push(imageFile);
      
      if (onProgress) {
        onProgress(pageNum, numPages);
      }
    }
    
    console.log(`PDF conversion complete: ${images.length} images created`);
    return images;
  } catch (error) {
    console.error('Error converting PDF to images:', error);
    throw new Error(`Failed to convert PDF: ${error.message}`);
  }
};

/**
 * Check if a file is a PDF
 * @param {File} file - File to check
 * @returns {boolean}
 */
export const isPdf = (file) => {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
};
